import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Loader2, Medal, Save } from "lucide-react";
import { TierBadge } from "@/components/oem/TierBadge";

/* Admin-editable OEM reference data: tier bands (and, over time, the KPI list)
   so Kia's tweaks never require a rebuild. */
export const OemScorecardTab = () => {
  const [bands, setBands] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/oem/config").then((r) => setBands(r.data.tier_bands || []))
      .catch(() => setBands([]));
  }, []);

  const setMin = (i, value) => setBands((prev) =>
    prev.map((b, j) => (j === i ? { ...b, min: value } : b)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = bands.map((b) => ({ ...b, min: Number(b.min) }));
      if (payload.some((b) => Number.isNaN(b.min))) {
        toast.error("Tier minimums must be numbers");
        setSaving(false);
        return;
      }
      const r = await api.put("/oem/config", { tier_bands: payload });
      setBands(r.data.tier_bands);
      toast.success("Tier bands saved", { description: "Badges, zones and tier counts recompute automatically." });
    } catch (e) {
      toast.error("Save failed", { description: errMsg(e) });
    }
    setSaving(false);
  };

  if (bands === null) return <Skeleton className="h-40" />;

  return (
    <Card data-testid="oem-settings-tab">
      <CardHeader className="pb-2">
        <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
          <Medal size={15} className="text-[hsl(var(--cx-gold))]" /> Kia CX Scorecard — Tier Bands
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-muted-foreground">
          Minimum Monthly Achievement per tier. Kia occasionally revises these — editing here
          updates tier badges, trend-chart zones and tier counts everywhere, without a rebuild.
        </p>
        <div className="space-y-2 max-w-sm">
          {bands.map((b, i) => (
            <div key={b.tier} className="flex items-center gap-3">
              <span className="w-24"><TierBadge tier={b.tier} /></span>
              <span className="text-xs text-muted-foreground w-8">≥</span>
              <Input type="number" className="h-8 w-28 text-sm tabular" value={b.min}
                onChange={(e) => setMin(i, e.target.value)}
                disabled={b.tier === "Focus"}
                data-testid={`oem-band-min-${b.tier.toLowerCase()}`} />
              {b.tier === "Focus" && <span className="text-[0.65rem] text-muted-foreground">everything below {bands[i - 1]?.min ?? "…"}</span>}
            </div>
          ))}
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="oem-band-save-btn">
          {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />}
          Save tier bands
        </Button>
        <p className="text-[0.65rem] text-muted-foreground">
          The KPI list itself is read from each uploaded file, so new or renamed OEM parameters
          flow in automatically with the next upload.
        </p>
      </CardContent>
    </Card>
  );
};
